// src/Components/HPC_Report/HPCReportPage.jsx - Updated with cover pages and fixed page numbering
import React, { useEffect, useRef, useState } from 'react';
import Layout from "../../Layout";
import AssessmentIconSVG from './svg/AssessmentIconSVG';
import { exportToPdf } from "./exportToPdf";
import SystemStatus from './components/SystemStatus';
import { API_CONFIG } from '../../config/api';

// Import cover pages for foundational report
// Import foundational report components
import {
    CoverPage1,
    CoverPage2,
    CoverPage3,
    CoverPage4,
    PartA1 as FoundationalPartA1,
    PartA2 as FoundationalPartA2,
    PartBDomainPage as FoundationalPartBDomainPage,
    PartC as FoundationalPartC,
    TeacherFeedbackPage as FoundationalTeacherFeedbackPage
} from "./foundational_report/report";
// Import middle report components
import {
    CaregiverObservationPage as MiddleCaregiverObservationPage,
    MiddleCoverPage1,
    MiddleCoverPage2,
    MiddleCoverPage3,
    MiddleCoverPage4,
    PartA1 as MiddlePartA1,
    PartA2 as MiddlePartA2,
    PartA3 as MiddlePartA3,
    PartBDomainPage as MiddlePartBDomainPage,
    PartC as MiddlePartC,
    PeerFeedbackPage as MiddlePeerFeedbackPage,
    SelfAssessmentPage as MiddleSelfAssessmentPage,
    TeacherFeedbackPage as MiddleTeacherFeedbackPage
} from "./middle_report/report";
// Import preparatory report components
import {
    CaregiverObservationPage1 as PreparatoryCaregiverObservationPage1,
    CaregiverObservationPage2 as PreparatoryCaregiverObservationPage2,
    PartA1 as PreparatoryPartA1,
    PartA2 as PreparatoryPartA2,
    PartBDomainPage as PreparatoryPartBDomainPage,
    PartC as PreparatoryPartC,
    PeerFeedbackPage as PreparatoryPeerFeedbackPage,
    PrepCoverPage1,
    PrepCoverPage2,
    PrepCoverPage3,
    PrepCoverPage4,
    SelfAssessmentPage as PreparatorySelfAssessmentPage,
    TeacherFeedbackPage as PreparatoryTeacherFeedbackPage
} from "./preparatory_report/report";

// Import secondary report components
import {
    CoverPage1 as SecondaryCoverPage1,
    CoverPage2 as SecondaryCoverPage2,
    CoverPage3 as SecondaryCoverPage3,
    CoverPage4 as SecondaryCoverPage4,
    PartA1 as SecondaryPartA1,
    PartA2 as SecondaryPartA2,
    PartA3 as SecondaryPartA3,
    PartA4 as SecondaryPartA4,
    PartA5 as SecondaryPartA5,
    PartA6 as SecondaryPartA6,
    PartBPage1 as SecondaryPartBPage1,
    PartBPage2 as SecondaryPartBPage2,
    PartBPage3 as SecondaryPartBPage3,
    PartBPage4 as SecondaryPartBPage4,
    PartBPage5 as SecondaryPartBPage5,
    PartBPage6 as SecondaryPartBPage6,
    PartBPage7 as SecondaryPartBPage7,
    PartBPage8 as SecondaryPartBPage8,
    PartCPage1 as SecondaryPartCPage1,
    PartCPage2 as SecondaryPartCPage2,
    PartCPage3 as SecondaryPartCPage3,
    PartCPage4 as SecondaryPartCPage4,
    PartCPage5 as SecondaryPartCPage5,
    PartCPage6 as SecondaryPartCPage6,
    PartCPage7 as SecondaryPartCPage7,
    PartCPage8 as SecondaryPartCPage8,
    PartCPage9 as SecondaryPartCPage9,
    PartCPage10 as SecondaryPartCPage10,
    PartDPage1 as SecondaryPartDPage1,
    PartDPage2 as SecondaryPartDPage2,
    PartDPage3 as SecondaryPartDPage3,
    PartDPage4 as SecondaryPartDPage4,
    PartDPage5 as SecondaryPartDPage5,
    PartE as SecondaryPartE,
    PartFPage1 as SecondaryPartFPage1,
    PartFPage2 as SecondaryPartFPage2,
    CompetencyProfilePage1 as SecondaryCompetencyProfilePage1,
    CompetencyProfilePage2 as SecondaryCompetencyProfilePage2
} from "./secondary_report/report";

// Import service for foundational stage
import {
    displayDate as formatFoundationalDate,
    fetchFoundationalReportData,
    fetchProfilePicture as fetchFoundationalProfilePicture
} from './services/foundationalReportService';

// Import service for preparatory stage (also contains the generic getUserStage)
import {
    displayDate as formatPreparatoryDate,
    fetchPreparatoryReportData,
    fetchProfilePicture as fetchPreparatoryProfilePicture,
    getUserStage,
    fetchPreGeneratedReport
} from './services/preparatoryReportService';

// Import service for middle stage
import {
    displayDate as formatMiddleDate,
    fetchMiddleStageHPCReportForStudent,
    fetchProfilePicture as fetchMiddleProfilePicture
} from "./services/middleReportService";

// Import service for secondary stage
import {
    displayDate as formatSecondaryDate,
    fetchSecondaryStageHPCReportForStudent,
    fetchProfilePicture as fetchSecondaryProfilePicture
} from "./services/secondaryReportService";

const HPCReportPage = () => {
    const [reportData, setReportData] = useState(null);
    const [studentImage, setStudentImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [userStage, setUserStage] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState("Loading student report data...");
    const [showSystemStatus, setShowSystemStatus] = useState(false);
    const reportRef = useRef(null);

    // Pre-generated (official) report state
    const [preGeneratedReport, setPreGeneratedReport] = useState(null);
    const [reportMode, setReportMode] = useState('official'); // 'official' | 'live'
    const [hasOfficialReport, setHasOfficialReport] = useState(false);

    // Fetch the user's educational stage and check for pre-generated report
    useEffect(() => {
        const fetchUserStage = async () => {
            try {
                setLoading(true);
                setLoadingMessage("Loading student report data...");

                // Fetch stage and pre-generated report in parallel
                const [stageData, preGenReport] = await Promise.all([
                    getUserStage(),
                    fetchPreGeneratedReport(),
                ]);

                setUserStage(stageData.stage);
                console.log("User stage:", stageData.stage);

                if (preGenReport && preGenReport.report_data) {
                    setPreGeneratedReport(preGenReport);
                    setHasOfficialReport(true);
                    setReportMode('official');
                    console.log("Pre-generated (official) report found, generated at:", preGenReport.generated_at);
                } else {
                    setHasOfficialReport(false);
                    setReportMode('live');
                }
            } catch (e) {
                console.error("Failed to fetch user stage:", e);
                setError(e.message || "An unknown error occurred while fetching the user's stage.");
                setUserStage("Foundational Stage");
            }
        };

        fetchUserStage();
    }, []);

    // Fetch report data based on user's stage (only for live mode)
    useEffect(() => {
        if (!userStage) return;
        // If we have an official report and user is viewing it, skip live fetch
        if (reportMode === 'official' && hasOfficialReport) {
            setLoading(false);
            return;
        }

        const fetchReportData = async () => {
            try {
                setLoading(true);
                setError(null);
                setLoadingMessage("Generating your HPC report...");

                let data;
                let profilePic;

                if (userStage === "Secondary Stage") {
                    setLoadingMessage("Generating Secondary Stage HPC report...");
                    data = await fetchSecondaryStageHPCReportForStudent();
                    console.log("Secondary Stage Data: ", data);
                    profilePic = await fetchSecondaryProfilePicture();
                    if (data.part_a_1 && data.part_a_1.date_of_birth) {
                        data.part_a_1.date_of_birth = formatSecondaryDate(data.part_a_1.date_of_birth);
                    }
                } else if (userStage === "Middle Stage") {
                    setLoadingMessage("Generating Middle Stage HPC report...");
                    data = await fetchMiddleStageHPCReportForStudent();
                    console.log("Middle Stage Data: ", data);
                    profilePic = await fetchMiddleProfilePicture();
                    if (data.part_a_1 && data.part_a_1.date_of_birth) {
                        data.part_a_1.date_of_birth = formatMiddleDate(data.part_a_1.date_of_birth);
                    }
                } else if (userStage === "Preparatory Stage") {
                    setLoadingMessage("Generating Preparatory Stage HPC report...");
                    data = await fetchPreparatoryReportData();
                    console.log("Preparatory Stage Data: ", data);
                    profilePic = await fetchPreparatoryProfilePicture();
                    if (data.part_a_1 && data.part_a_1.date_of_birth) {
                        data.part_a_1.date_of_birth = formatPreparatoryDate(data.part_a_1.date_of_birth);
                    }
                } else {
                    setLoadingMessage("Generating Foundational Stage HPC report...");
                    data = await fetchFoundationalReportData();
                    console.log("Foundation Report Data: ", data);

                    profilePic = await fetchFoundationalProfilePicture();
                    if (data.part_a_1 && data.part_a_1.date_of_birth) {
                        data.part_a_1.date_of_birth = formatFoundationalDate(data.part_a_1.date_of_birth);
                    }
                }

                setLoadingMessage("Loading profile picture...");
                if (profilePic) setStudentImage(profilePic);

                if (!data || Object.keys(data).length === 0) {
                    setReportData({});
                } else {
                    const processedData = { ...data };
                    if (processedData.part_a_1 && !processedData.part_a_1.student_name) {
                        processedData.part_a_1.student_name = "Student";
                    }
                    setReportData(processedData);
                }
            } catch (e) {
                console.error("Failed to fetch report data:", e);
                let errorMessage = "An unknown error occurred while fetching the report.";

                if (e.message.includes("Failed to fetch")) {
                    errorMessage = "Network error: Unable to connect to the server. Please check your internet connection and try again.";
                } else if (e.message.includes("CORS")) {
                    errorMessage = "Connection error: Please try refreshing the page or contact support if the issue persists.";
                } else if (e.message.includes("504") || e.message.includes("Gateway Time-out")) {
                    errorMessage = "Server timeout: The report is taking longer than expected to generate. Please try again in a few moments.";
                } else if (e.message.includes("Authentication required")) {
                    errorMessage = "Session expired: Please log in again to access your report.";
                } else {
                    errorMessage = e.message;
                }

                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [userStage, reportMode, hasOfficialReport]);

    // Handle PDF export
    const handleExportPdf = async () => {
        if (reportRef.current) {
            setExportingPdf(true);
            try {
                const isSecondary = userStage === "Secondary Stage";
                const isMiddle = userStage === "Middle Stage";
                const isPreparatory = userStage === "Preparatory Stage";
                const stagePrefix = isSecondary ? "Secondary_" : (isMiddle ? "Middle_" : (isPreparatory ? "Preparatory_" : "Foundational_"));

                await exportToPdf(
                    'hpc-report-content',
                    `${stagePrefix}HPC_Report_${reportData?.part_a_1?.student_name?.replace(/\s/g, '_') || 'Student'}.pdf`
                );
            } catch (error) {
                console.error("Error exporting PDF:", error);
                alert("There was an error generating the PDF. Please try again.");
            } finally {
                setExportingPdf(false);
            }
        }
    };

    // Page counter ref for dynamic page numbering - REMOVED as requested
    // const pageCounter = useRef(0);

    if (loading) {
        return <Layout pageTitle="Student Report">
            <div className="flex flex-col justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
                <p className="text-lg font-semibold text-gray-700">{loadingMessage}</p>
                <p className="text-sm text-gray-500 mt-2">This may take a few minutes...</p>
            </div>
        </Layout>;
    }

    if (error) {
        return <Layout pageTitle="Student Report">
            <div className="p-8 text-center">
                <h2 className="text-xl text-red-600 mb-4">Error Loading Report</h2>
                <p className="text-gray-700 mb-6">{error}</p>
                <div className="space-x-4">
                    <button
                        onClick={() => {
                            setError(null);
                            setLoading(true);
                            // Trigger a re-fetch by updating the userStage
                            const currentStage = userStage;
                            setUserStage(null);
                            setTimeout(() => setUserStage(currentStage), 100);
                        }}
                        className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded shadow-md"
                    >
                        Try Again
                    </button>
                    <button
                        onClick={() => setShowSystemStatus(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded shadow-md"
                    >
                        System Status
                    </button>
                </div>
            </div>
        </Layout>;
    }

    // Check for supported stages
    if (!userStage || (userStage !== "Foundational Stage" && userStage !== "Preparatory Stage" && userStage !== "Middle Stage" && userStage !== "Secondary Stage")) {
        return (
            <Layout pageTitle="Student Report">
                <div className="p-8 text-center font-[poppins]">
                    <h2 className="text-xl font-semibold mb-2">Report Not Available</h2>
                    <p className="text-gray-700 mb-4">
                        Performance reports are currently only available for Foundational, Preparatory, Middle, and Secondary Stage
                        students.
                    </p>
                    <p className="text-sm text-gray-500">
                        Your current stage: {userStage || "Unknown"}
                    </p>
                </div>
            </Layout>
        );
    }

    // Determine which data source to use based on report mode
    const activeReportData = (reportMode === 'official' && preGeneratedReport)
        ? preGeneratedReport.report_data
        : reportData;
    const activeStage = (reportMode === 'official' && preGeneratedReport)
        ? preGeneratedReport.stage
        : userStage;
    const schoolBranding = (reportMode === 'official' && preGeneratedReport)
        ? preGeneratedReport.school_branding
        : null;

    // Format date_of_birth for official report (pre-generated data stores raw ISO dates)
    if (reportMode === 'official' && activeReportData?.part_a_1?.date_of_birth) {
        activeReportData.part_a_1.date_of_birth = formatFoundationalDate(activeReportData.part_a_1.date_of_birth);
    }

    const dataForPartA1 = activeReportData?.part_a_1 || null;
    const dataForPartA2 = activeReportData?.part_a_2 || null;
    const dataForPartA3 = activeReportData?.part_a_3 || null;
    const dataForPartA4 = activeReportData?.part_a_4 || null;
    const dataForPartA5 = activeReportData?.part_a_5 || null;
    const dataForPartA6 = activeReportData?.part_a_6 || null;
    const dataForPartB = activeReportData?.part_b || [];
    const dataForPartC = activeReportData?.part_c || [];
    const dataForPartD = activeReportData?.part_d || [];
    const dataForPartE = activeReportData?.part_e || [];
    const dataForPartF = activeReportData?.part_f || [];

    const isSecondary = (activeStage || userStage) === "Secondary Stage";
    const isPreparatory = (activeStage || userStage) === "Preparatory Stage";
    const isMiddle = (activeStage || userStage) === "Middle Stage";
    const isFoundational = (activeStage || userStage) === "Foundational Stage";

    // Debug logging for Secondary Stage Part B
    if (isSecondary) {
        console.log('Secondary Stage HPC Report Data:', reportData);
        console.log('Part B Data:', dataForPartB);
        console.log('Part B Length:', dataForPartB.length);
        if (dataForPartB.length > 0) {
            console.log('First Part B Entry:', dataForPartB[0]);
        }
        console.log('Part E Data:', dataForPartE);
        console.log('Part E Length:', dataForPartE.length);
        if (dataForPartE.length > 0) {
            console.log('First Part E Entry:', dataForPartE[0]);
        }
    }

    // Component selection based on stage
    const PartA1 = isMiddle ? MiddlePartA1 : (isPreparatory ? PreparatoryPartA1 : FoundationalPartA1);
    const PartA2 = isMiddle ? MiddlePartA2 : (isPreparatory ? PreparatoryPartA2 : FoundationalPartA2);
    const PartA3 = isMiddle ? MiddlePartA3 : () => null;
    const PartBDomainPage = isMiddle ? MiddlePartBDomainPage : (isPreparatory ? PreparatoryPartBDomainPage : FoundationalPartBDomainPage);
    const TeacherFeedbackPage = isMiddle ? MiddleTeacherFeedbackPage : (isPreparatory ? PreparatoryTeacherFeedbackPage : FoundationalTeacherFeedbackPage);
    const SelfAssessmentPage = isMiddle ? MiddleSelfAssessmentPage : (isPreparatory ? PreparatorySelfAssessmentPage : null);
    const PeerFeedbackPage = isMiddle ? MiddlePeerFeedbackPage : (isPreparatory ? PreparatoryPeerFeedbackPage : null);
    const PartC = isMiddle ? MiddlePartC : (isPreparatory ? PreparatoryPartC : FoundationalPartC);

    // Handle switching to live mode — triggers live data fetch
    const handleSwitchToLive = () => {
        setReportMode('live');
        if (!reportData) {
            setLoading(true);
        }
    };

    const apiBaseUrl = API_CONFIG.BASE_URL;

    return (
        <Layout pageTitle="Student Performance Report">
            <div className="py-8 bg-gray-100 min-h-screen font-[poppins]" style={{ padding: '2rem 0' }}>
                {/* Header bar */}
                <div
                    className="max-w-[210mm] mx-auto mb-4 flex justify-between items-center bg-white p-4 rounded-lg shadow-lg">
                    <div className="flex items-center">
                        <div className="bg-orange-600 text-white p-2 rounded-full mr-3">
                            <AssessmentIconSVG type="books" size={24} selected={true} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                {isSecondary ? "Secondary Stage Performance Report" : isMiddle ? "Middle Stage Performance Report" : isPreparatory ? "Preparatory Stage Performance Report" : "Foundational Stage Performance Report"}
                            </h2>
                            <p className="text-gray-600 text-sm">Student: <span
                                className="font-semibold">{dataForPartA1?.student_name || 'Student'}</span>
                                {hasOfficialReport && preGeneratedReport?.generated_at && (
                                    <span className="text-gray-400 ml-2">· Generated: {new Date(preGeneratedReport.generated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                )}
                            </p>
                        </div>
                    </div>
                    <button onClick={handleExportPdf} disabled={(!activeReportData) || exportingPdf}
                        className="no-print bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded shadow-md flex items-center disabled:opacity-50">
                        <AssessmentIconSVG type="computer" size={18} selected={true} />
                        <span className="ml-2">{exportingPdf ? 'Generating...' : 'Export as PDF'}</span>
                    </button>
                </div>

                {/* Loading state for live mode switch */}
                {reportMode === 'live' && loading && (
                    <div className="max-w-[210mm] mx-auto">
                        <div className="flex flex-col justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
                            <p className="text-lg font-semibold text-gray-700">{loadingMessage}</p>
                            <p className="text-sm text-gray-500 mt-2">This may take a few minutes...</p>
                        </div>
                    </div>
                )}

                {/* Report content — render when data is available */}
                {!(reportMode === 'live' && loading) && (
                <div id="hpc-report-content" ref={reportRef}
                    style={{ display: 'block', margin: '0 auto', padding: '0', maxWidth: '210mm' }}>

                    {/* School Logo Page (branding front page) — official report only */}
                    {reportMode === 'official' && schoolBranding?.school_logo_url && (
                        <div style={{ marginBottom: '2rem' }}>
                            <div className="report-page" style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                padding: '30mm 25mm',
                                minHeight: '297mm'
                            }}>
                                {/* Logo - Centered */}
                                <div style={{ marginBottom: '15mm', textAlign: 'center' }}>
                                    <img 
                                        src={schoolBranding.school_logo_url.startsWith('http') ? schoolBranding.school_logo_url : `${apiBaseUrl}${schoolBranding.school_logo_url}`}
                                        alt="School Logo" 
                                        style={{ 
                                            maxWidth: '70mm', 
                                            maxHeight: '70mm', 
                                            objectFit: 'contain',
                                            display: 'block',
                                            margin: '0 auto'
                                        }} 
                                    />
                                </div>

                                {/* School Name - Centered */}
                                {schoolBranding.school_name && (
                                    <h1 style={{ 
                                        fontSize: '28pt', 
                                        fontWeight: 'bold', 
                                        color: '#1a1a1a', 
                                        margin: '0 0 6mm 0', 
                                        textAlign: 'center', 
                                        fontFamily: 'Poppins, sans-serif',
                                        lineHeight: '1.3'
                                    }}>
                                        {schoolBranding.school_name}
                                    </h1>
                                )}

                                {/* Address - Centered */}
                                {schoolBranding.school_address && (
                                    <p style={{ 
                                        fontSize: '13pt', 
                                        color: '#666', 
                                        margin: '0 0 15mm 0', 
                                        textAlign: 'center', 
                                        lineHeight: '1.6',
                                        maxWidth: '140mm'
                                    }}>
                                        {schoolBranding.school_address}
                                    </p>
                                )}

                                {/* Divider - Centered */}
                                <div style={{ 
                                    width: '70mm', 
                                    height: '2px', 
                                    backgroundColor: '#F97316', 
                                    margin: '0 auto 15mm auto'
                                }}></div>

                                {/* HPC Title - Centered */}
                                <h2 style={{ 
                                    fontSize: '24pt', 
                                    fontWeight: 'bold', 
                                    color: '#F97316', 
                                    margin: '0 0 10mm 0', 
                                    textAlign: 'center', 
                                    fontFamily: 'Poppins, sans-serif'
                                }}>
                                    Holistic Progress Card
                                </h2>

                                {/* Academic Year - Centered */}
                                {schoolBranding.academic_year && (
                                    <p style={{ 
                                        fontSize: '16pt', 
                                        fontWeight: '600', 
                                        color: '#444', 
                                        margin: 0, 
                                        textAlign: 'center'
                                    }}>
                                        Academic Year: {schoolBranding.academic_year}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}                    {/* Cover Pages for All Stages */}
                    {isFoundational && (
                        <>
                            <div style={{ marginBottom: '2rem' }}>
                                <CoverPage1 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <CoverPage2 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <CoverPage3 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <CoverPage4 />
                            </div>
                        </>
                    )}

                    {/* Cover Pages for Middle Stage */}
                    {isMiddle && (
                        <>
                            <div style={{ marginBottom: '2rem' }}>
                                <MiddleCoverPage1 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <MiddleCoverPage2 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <MiddleCoverPage3 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <MiddleCoverPage4 />
                            </div>
                        </>
                    )}

                    {/* Cover Pages for Preparatory Stage */}
                    {isPreparatory && (
                        <>
                            <div style={{ marginBottom: '2rem' }}>
                                <PrepCoverPage1 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <PrepCoverPage2 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <PrepCoverPage3 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <PrepCoverPage4 />
                            </div>
                        </>
                    )}

                    {/* Cover Pages for Secondary Stage */}
                    {isSecondary && (
                        <>
                            <div style={{ marginBottom: '2rem' }}>
                                <SecondaryCoverPage1 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <SecondaryCoverPage2 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <SecondaryCoverPage3 />
                            </div>
                            <div style={{ marginBottom: '2rem' }}>
                                <SecondaryCoverPage4 />
                            </div>
                        </>
                    )}

                    {/* Main Report Content */}
                    {dataForPartA1 && <div style={{ marginBottom: '2rem' }}>
                        {isSecondary ? (
                            <SecondaryPartA1 data={dataForPartA1} studentImage={studentImage} />
                        ) : (
                            <PartA1 data={dataForPartA1} studentImage={studentImage} />
                        )}
                    </div>}

                    {/* Secondary Stage Parts A2-A6 */}
                    {isSecondary && (
                        <>
                            {dataForPartA2 && (
                                <>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA2 data={dataForPartA2} pageNumber={1} />
                                    </div>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA2 data={dataForPartA2} pageNumber={2} />
                                    </div>
                                </>
                            )}

                            {dataForPartA3 && (
                                <>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA3 data={dataForPartA3} pageNumber={1} />
                                    </div>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA3 data={dataForPartA3} pageNumber={2} />
                                    </div>
                                </>
                            )}

                            {dataForPartA4 && (
                                <>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA4 data={dataForPartA4} pageNumber={1} />
                                    </div>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA4 data={dataForPartA4} pageNumber={2} />
                                    </div>
                                </>
                            )}

                            {dataForPartA5 && (
                                <div style={{ marginBottom: '2rem' }}>
                                    <SecondaryPartA5 data={dataForPartA5} />
                                </div>
                            )}

                            {dataForPartA6 && (
                                <>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA6 data={dataForPartA6} pageNumber={1} />
                                    </div>
                                    <div style={{ marginBottom: '2rem' }}>
                                        <SecondaryPartA6 data={dataForPartA6} pageNumber={2} />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Other Stages Parts A2-A3 */}
                    {!isSecondary && dataForPartA2 && <div style={{ marginBottom: '2rem' }}>
                        <PartA2 data={dataForPartA2} />
                    </div>}

                    {/* Conditionally render PartA3 for Middle Stage */}
                    {isMiddle && dataForPartA3 && (
                        <div style={{ marginBottom: '2rem' }}>
                            <PartA3 data={dataForPartA3} />
                        </div>
                    )}

                    {/* Part B - Domain Pages */}
                    {isSecondary ? (
                        // Secondary Stage Part B - Custom 8-page format
                        dataForPartB.length > 0 ? (
                            dataForPartB.map((domainEntry) => (
                                domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                    domainEntry.assignments.map((assignment, i) => (
                                        <React.Fragment key={`${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                            {/* Page 1: Project Details */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage1 data={assignment.part_b?.page_1} />
                                            </div>

                                            {/* Page 2: Stage 1 Student Reflection */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage2 data={assignment.part_b?.page_2} />
                                            </div>

                                            {/* Page 3: Stage 1 Teacher Assessment */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage3 data={assignment.part_b?.page_3} />
                                            </div>

                                            {/* Page 4: Stage 2 Teacher Assessment */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage4 data={assignment.part_b?.page_4} />
                                            </div>

                                            {/* Page 5: Stage 3 Learner Reflection & Peer Feedback */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage5 data={assignment.part_b?.page_5} />
                                            </div>

                                            {/* Page 6: Stage 3 Rubric */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage6 data={assignment.part_b?.page_6} />
                                            </div>

                                            {/* Page 7: Performance Summary */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage7 data={assignment.part_b} />
                                            </div>

                                            {/* Page 8: Post-project Reflections */}
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SecondaryPartBPage8 data={assignment.part_b} />
                                            </div>
                                        </React.Fragment>
                                    ))
                                ) : null
                            ))
                        ) : (
                            <div style={{
                                marginBottom: '2rem',
                                padding: '40px',
                                textAlign: 'center',
                                backgroundColor: '#f9f9f9',
                                border: '2px dashed #ccc',
                                borderRadius: '8px'
                            }}>
                                <h3 style={{ color: '#666', marginBottom: '10px' }}>No Part B Activities Yet</h3>
                                <p style={{ color: '#999' }}>Domain activities will appear here once your teacher assigns them.</p>
                            </div>
                        )
                    ) : (
                        // Other Stages Part B - Original format
                        dataForPartB.map((domainEntry) => (
                            domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                domainEntry.assignments.map((assignment, i) => (
                                    <React.Fragment key={`${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                        {/* Part B Domain Page */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <PartBDomainPage
                                                domain={domainEntry}
                                                assignment={assignment}
                                            />
                                        </div>

                                        {/* Teacher Feedback Page - Always show */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <TeacherFeedbackPage
                                                assignment={assignment}
                                                proficiencyMarks={assignment.proficiency_marks}
                                                observationalNotes={assignment.observational_notes || assignment.teacher_feedback}
                                            />
                                        </div>

                                        {/* Self Assessment Page - Show if data exists OR for preparatory/middle stages */}
                                        {(isPreparatory || isMiddle) && (
                                            <div style={{ marginBottom: '2rem' }}>
                                                <SelfAssessmentPage
                                                    assignment={assignment}
                                                />
                                            </div>
                                        )}

                                        {/* Peer Assessment Page - Show if data exists OR for preparatory/middle stages */}
                                        {(isPreparatory || isMiddle) && (
                                            <div style={{ marginBottom: '2rem' }}>
                                                <PeerFeedbackPage
                                                    assignment={assignment}
                                                />
                                            </div>
                                        )}

                                        {/* Caregiver Observation Pages - Show if data exists OR for preparatory/middle stages */}
                                        {isPreparatory && (
                                            <>
                                                <div style={{ marginBottom: '2rem' }}>
                                                    <PreparatoryCaregiverObservationPage1
                                                        assignment={assignment}
                                                    />
                                                </div>
                                                <div style={{ marginBottom: '2rem' }}>
                                                    <PreparatoryCaregiverObservationPage2
                                                        assignment={assignment}
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {isMiddle && (
                                            <div style={{ marginBottom: '2rem' }}>
                                                <MiddleCaregiverObservationPage
                                                    assignment={assignment}
                                                />
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : null
                        ))
                    )}

                    {/* Part C - Summary for the Academic Year (Foundational/Preparatory/Middle) */}
                    {!isSecondary && dataForPartC && dataForPartC.length > 0 && (
                        <div style={{ marginBottom: '2rem' }}>
                            <PartC data={dataForPartC} />
                        </div>
                    )}

                    {/* Part C - Problem-Based Inquiry Pages (Secondary) */}
                    {isSecondary && dataForPartC.length > 0 && (
                        dataForPartC.map((domainEntry) => (
                            domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                domainEntry.assignments.map((assignment, i) => (
                                    <React.Fragment key={`part_c_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                        {/* Part C Page 1: Project Details */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage1 data={assignment.part_c?.page_1} />
                                        </div>

                                        {/* Part C Page 2: Research Process */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage2 data={assignment.part_c?.page_2} />
                                        </div>

                                        {/* Part C Page 3: Stage 1 Teacher Assessment */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage3 data={assignment.part_c?.page_3} />
                                        </div>

                                        {/* Part C Page 4: Stage 1 Learner Reflection */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage4 data={assignment.part_c?.page_4} />
                                        </div>

                                        {/* Part C Page 5: Stage 2 Teacher Assessment */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage5 data={assignment.part_c?.page_5} />
                                        </div>

                                        {/* Part C Page 6: Stage 2 Learner Reflection */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage6 data={assignment.part_c?.page_6} />
                                        </div>

                                        {/* Part C Page 7: Stage 3 Peer Feedback (visual page 26) */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage7 data={assignment.part_c?.page_8} />
                                        </div>

                                        {/* Part C Page 8: Stage 3 Teacher Assessment (visual page 25) */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage8 data={assignment.part_c?.page_7} />
                                        </div>

                                        {/* Part C Page 9: Overview */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage9 data={assignment.part_c?.page_9} />
                                        </div>

                                        {/* Part C Page 10: Post-inquiry Reflections */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartCPage10 data={assignment.part_c?.page_10} />
                                        </div>
                                    </React.Fragment>
                                ))
                            ) : null
                        ))
                    )}

                    {/* Part D - Observation Template for Classroom Interactions */}
                    {isSecondary && dataForPartD.length > 0 && (
                        dataForPartD.map((domainEntry) => (
                            domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                domainEntry.assignments.map((assignment, i) => (
                                    <React.Fragment key={`part_d_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                        {/* Part D Page 1: Observation Template Setup */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartDPage1 data={assignment.part_d?.page_1} />
                                        </div>

                                        {/* Part D Page 2: Teacher Assessment */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartDPage2 data={assignment.part_d?.page_2} />
                                        </div>

                                        {/* Part D Page 3: Learner Reflection */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartDPage3 data={assignment.part_d?.page_3} />
                                        </div>

                                        {/* Part D Page 4: Peer Feedback */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartDPage4 data={assignment.part_d?.page_4} />
                                        </div>

                                        {/* Part D Page 5: Overview */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartDPage5 data={assignment.part_d?.page_5} />
                                        </div>


                                    </React.Fragment>
                                ))
                            ) : null
                        ))
                    )}

                    {/* Part E - Learning through Online Courses */}
                    {isSecondary && dataForPartE.length > 0 && (
                        dataForPartE.map((domainEntry) => (
                            domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                domainEntry.assignments.map((assignment, i) => (
                                    <React.Fragment key={`part_e_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                        {/* Part E: Learning through Online Courses */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartE data={assignment.part_e?.student_fields} />
                                        </div>
                                    </React.Fragment>
                                ))
                            ) : null
                        ))
                    )}

                    {/* Part F - Number of Hours Spent by the Learner */}
                    {isSecondary && dataForPartF.length > 0 && (
                        dataForPartF.map((domainEntry) => (
                            domainEntry.assignments && domainEntry.assignments.length > 0 ? (
                                domainEntry.assignments.map((assignment, i) => (
                                    <React.Fragment key={`part_f_${domainEntry.domain_id}_${assignment.assignment_id}`}>
                                        {/* Part F Page 1: Hours Tracking */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartFPage1 data={assignment.part_f?.page_1} />
                                        </div>

                                        {/* Part F Page 2: Skills Training */}
                                        <div style={{ marginBottom: '2rem' }}>
                                            <SecondaryPartFPage2 data={assignment.part_f?.page_2} />
                                        </div>

                                        {/* Competency Profile - Show if data exists */}
                                        {assignment.competency_profile && (
                                            <>
                                                {/* Competency Profile Page 1: Performance Level Descriptors */}
                                                <div style={{ marginBottom: '2rem' }}>
                                                    <SecondaryCompetencyProfilePage1 data={assignment.competency_profile?.page_1} />
                                                </div>

                                                {/* Competency Profile Page 2: Additional Notes */}
                                                <div style={{ marginBottom: '2rem' }}>
                                                    <SecondaryCompetencyProfilePage2 data={assignment.competency_profile?.page_1} />
                                                </div>
                                            </>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : null
                        ))
                    )}

                    {/* Principal Signature Page (branding end page) — official report only */}
                    {reportMode === 'official' && schoolBranding?.principal_signature_url && (
                        <div style={{ marginBottom: '2rem' }}>
                            <div className="report-page" style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                padding: '25mm 30mm 20mm 30mm',
                                minHeight: '297mm'
                            }}>
                                {/* Spacer - pushes signature to lower portion */}
                                <div style={{ flex: 1 }}></div>

                                {/* Signature block - right aligned */}
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'flex-end', 
                                    marginBottom: '35mm',
                                    paddingRight: '10mm'
                                }}>
                                    <div style={{ textAlign: 'center', minWidth: '70mm' }}>
                                        {/* Signature Image */}
                                        <img
                                            src={schoolBranding.principal_signature_url.startsWith('http') ? schoolBranding.principal_signature_url : `${apiBaseUrl}${schoolBranding.principal_signature_url}`}
                                            alt="Principal Signature"
                                            style={{ 
                                                maxWidth: '60mm', 
                                                maxHeight: '25mm', 
                                                objectFit: 'contain', 
                                                display: 'block', 
                                                margin: '0 auto 4mm auto' 
                                            }}
                                        />
                                        {/* Signature Line and Text */}
                                        <div style={{ 
                                            borderTop: '2px solid #333', 
                                            paddingTop: '4mm',
                                            width: '70mm',
                                            margin: '0 auto'
                                        }}>
                                            <p style={{ 
                                                fontSize: '13pt', 
                                                fontWeight: 'bold', 
                                                color: '#1a1a1a', 
                                                margin: '0 0 2mm 0', 
                                                fontFamily: 'Poppins, sans-serif',
                                                textAlign: 'center'
                                            }}>
                                                {schoolBranding.principal_name || 'Principal'}
                                            </p>
                                            {schoolBranding.principal_designation && (
                                                <p style={{ 
                                                    fontSize: '11pt', 
                                                    color: '#666', 
                                                    margin: 0,
                                                    textAlign: 'center'
                                                }}>
                                                    {schoolBranding.principal_designation}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* School footer - centered */}
                                <div style={{ 
                                    textAlign: 'center', 
                                    borderTop: '1.5px solid #ddd', 
                                    paddingTop: '8mm',
                                    marginTop: 'auto'
                                }}>
                                    {schoolBranding.school_name && (
                                        <p style={{ 
                                            fontSize: '12pt', 
                                            fontWeight: '600', 
                                            color: '#333', 
                                            margin: '0 0 2mm 0', 
                                            fontFamily: 'Poppins, sans-serif'
                                        }}>
                                            {schoolBranding.school_name}
                                        </p>
                                    )}
                                    {schoolBranding.school_address && (
                                        <p style={{ 
                                            fontSize: '10pt', 
                                            color: '#777', 
                                            margin: 0,
                                            lineHeight: '1.4'
                                        }}>
                                            {schoolBranding.school_address}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>

            {showSystemStatus && (
                <SystemStatus onClose={() => setShowSystemStatus(false)} />
            )}
        </Layout>
    );
};

export default HPCReportPage;